import type { eq } from '@code-expert/prelude';
import { either, iots, pipe, string } from '@code-expert/prelude';

/**
 * The legi ("matriculation number") is a unique number assigned to each student when they
 * matriculate the first time to a Swiss university or university of applied sciences or
 * teacher education university.
 *
 * The number has eight digits. The first two digits represent the year of the first
 * matriculation. The next five digits are number blocks reserved for each of the
 * universities. The last digit is a check digit.
 *
 * Legis encoded as {@link Legi} are stored as formatted strings.
 *
 * @example "11-222-333"
 */
export type Legi = iots.TypeOf<typeof Legi>;

interface LegiBrand {
  readonly Legi: unique symbol;
}

export const Legi = iots.withMessage(
  iots.brand(
    iots.string,
    (u): u is iots.Branded<string, LegiBrand> => /^\d{2}-\d{3}-\d{3}$/.test(u),
    'Legi',
  ),
  () => 'Invalid Legi',
);

export const eqLegi: eq.Eq<Legi> = string.Eq;

/**
 * Decode from a numeric legi-string to a branded {@link Legi}.
 *
 * @example "11222333" is parsed into "11-222-333"
 */
export const LegiFromString = new iots.Type<Legi, string, unknown>(
  'LegiFromString',
  Legi.is,
  (input, context) =>
    pipe(
      iots.string.validate(input, context),
      either.chain((s) => Legi.decode(s.replace(/^(\d{2})(\d{3})(\d{3})$/, '$1-$2-$3'))),
    ),
  (l) => l.replace(/-/g, ''),
);

/**
 * Decode from a valid legi-string to a branded {@link Legi} or from `null | undefined | ''` to `undefined`
 */
export const NullableLegiFromString = new iots.Type<Legi | undefined, string | undefined, unknown>(
  'LegiFromString',
  Legi.is,
  (input, context) =>
    pipe(
      LegiFromString.validate(input, context),
      either.fold(
        (e) => (input == null || input === '' ? either.right(undefined) : either.left(e)),
        (x) => either.right(x),
      ),
    ),
  (l) => (typeof l === 'string' ? LegiFromString.encode(l) : undefined),
);
